<script lang="ts">
	import { onMount } from 'svelte';

	export let src: string;
	export let alt: string;
	export let loading: 'lazy' | 'eager' = 'lazy';
	export let sizes = '(min-width: 768px) 100vw, 50vw';
	let className: string = '';
	export { className as class };
	export let width: number | undefined = undefined;
	export let height: number | undefined = undefined;
	export let responsive = false;
	export let optimize = false;

	let containerElement: HTMLElement;
	let isLoaded = false;
	let isError = false;
	let shouldLoad = false;

	// Generate optimized image sources
	$: optimizedSrc = responsive ? `${src}?responsive` : optimize ? `${src}?optimize` : src;

	// Intersection Observer for lazy loading
	onMount(() => {
		if (loading === 'eager') {
			shouldLoad = true;
			return;
		}

		if (loading === 'lazy' && 'IntersectionObserver' in window) {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							shouldLoad = true;
							observer.unobserve(entry.target);
						}
					});
				},
				{ threshold: 0.1, rootMargin: '50px' }
			);

			if (containerElement) {
				observer.observe(containerElement);
			}

			return () => {
				observer.disconnect();
			};
		} else {
			// Fallback for browsers without IntersectionObserver
			shouldLoad = true;
		}
	});

	function handleLoad() {
		isLoaded = true;
	}

	function handleError() {
		isError = true;
	}
</script>

<div
	bind:this={containerElement}
	class="relative {className}"
	style={width && height ? `width: ${width}px; height: ${height}px;` : ''}
>
	<!-- Loading placeholder -->
	{#if !isLoaded && !isError && shouldLoad}
		<div
			class="absolute inset-0 animate-pulse bg-gray-200 dark:bg-gray-700"
			role="img"
			aria-label="Loading {alt}"
		></div>
	{/if}

	<!-- Error state -->
	{#if isError}
		<div
			class="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
			role="img"
			aria-label="Error loading {alt}"
		>
			<svg class="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
				/>
			</svg>
		</div>
	{/if}

	<!-- Actual image -->
	{#if shouldLoad}
		<img
			src={optimizedSrc}
			{alt}
			{loading}
			{sizes}
			{width}
			{height}
			class="{isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300"
			on:load={handleLoad}
			on:error={handleError}
		/>
	{/if}
</div>
